import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router, UrlMatchResult, UrlSegment } from '@angular/router';

/**
  * Handles the report "Portal page" short link. Two URL shapes reach this component:
 *   - {WebUrl}/{token}    (current) - matched by shortLinkTokenMatcher, below
 *   - {WebUrl}/r/{token}  (legacy)  - matched by the 'r/:token' route in AppRoutingModule,
 *                                     kept so links in already-generated PDFs keep working.
 *
 * Keep the alphabet and bit layout below in sync with the C# ReportShortLinkToken.
 */
const RESERVED_ROUTE_SEGMENTS = new Set<string>([
  'request-pdf', 'r', 'login', 'logout', 'verisk-logged-out', 'unauthorized',
  'forgotpassword', 'resetpassword', 'client', 'reset-password-response', 'signup',
  'setnewpassword', 'code-verification', 'utilization', 'profile-setting', 'history',
  'revocation', 'requests', 'workbench', 'staff', 'invoices', 'clients', 'user', 'team',
  'parameters', 'feedback', 'media-forensics', 'linkout',
]);

/**
 * Registered at the top level in app-routing.module.ts (no auth guard), next to the
 * legacy `r/:token` route.
 */
export function shortLinkTokenMatcher(segments: UrlSegment[]): UrlMatchResult | null {
  if (segments.length !== 1) { return null; }
  const seg = segments[0].path;
  if (RESERVED_ROUTE_SEGMENTS.has(seg)) { return null; }
  if (!/^[0-9A-Za-z]{6,11}$/.test(seg)) { return null; }
  return { consumed: segments, posParams: { token: segments[0] } };
}

@Component({
  selector: 'app-short-link-redirect',
  standalone: true,
  template: '',
})
export class ShortLinkRedirectComponent implements OnInit {
  private static readonly ALPHABET =
    '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';

  constructor(private route: ActivatedRoute, private router: Router) {}

  ngOnInit(): void {
    const decoded = ShortLinkRedirectComponent.decode(
      this.route.snapshot.paramMap.get('token') || ''
    );

    if (!decoded) {
      this.router.navigateByUrl('/requests', { replaceUrl: true });
      return;
    }

    const path = decoded.isPreview
      ? '/workbench/workbench-details'
      : '/requests/request-details';

    // Build the URL string explicitly. router.navigate([...]) would treat
    // 'workbench/workbench-details' as one encoded segment, which matches no
    // route and falls through to the '**' -> utilization redirect.
    const target =
      `${path}?inClaimMonitorId=${decoded.inClaimMonitorId}` +
      `&inClaimIterationId=${decoded.inClaimIterationId}`;

    this.router.navigateByUrl(target, { replaceUrl: true });
  }

  private static decode(
    token: string
  ): { inClaimMonitorId: number; inClaimIterationId: number; isPreview: boolean } | null {
    if (!token || token.length > 11) {
      return null;
    }

    let value = 0n;
    for (const ch of token) {
      const digit = ShortLinkRedirectComponent.ALPHABET.indexOf(ch);
      if (digit < 0) {
        return null;
      }
      value = value * 62n + BigInt(digit);
    }

    const mask = 0x7fffffffn; // 31 bits per id
    const inClaimMonitorId = Number((value >> 32n) & mask);
    const inClaimIterationId = Number((value >> 1n) & mask);
    const isPreview = (value & 1n) === 1n;

    if (inClaimMonitorId <= 0 || inClaimIterationId <= 0) {
      return null;
    }

    return { inClaimMonitorId, inClaimIterationId, isPreview };
  }
}
